import React, { useContext, useState, useEffect, useRef } from 'react'
import classNames from 'classnames'
import { Grid, GridItem } from '@deal/components'
import { useNavigationContext } from '#src/components/Header/StandardHeader/NavigationContext'
import { useFullBrowseNavHierarchyQuery } from '#src/components/Header/StandardHeader/FullBrowseNavHierarchy.generated'
import { DepartmentNavigationLinkFragment } from '#src/components/Header/NavHierarchyFragments.generated'
import { useMobileJournalNavigationQuery } from '../MobileJournalNavigation.generated'
import { useHistory } from 'react-router-dom'
import { serializeSearchQuery, getQueryParamsFromDeptNavLink } from '#src/services/search'
import { generateCreateUrl } from '#src/services/path'
import { IdentityContext } from '#src/containers/Identity'
import { generateReferAFriendUrl } from '#src/services/referrals'
import { AnalyticsContext } from '#src/containers/Analytics'
import { MobileNavigationItem } from '../../../types'
import NavigationList from './NavigationList'
import constructFlatDepartments from '../../../helpers/constructFlatDepartments'
import keyBy from 'lodash/keyBy'
import Search from './Search'
import ScrollLock from 'react-scrolllock'
import AccountMenu from './AccountMenu'
import ActiveCategoriesMenu from './ActiveCategoriesMenu'
import ActiveSubSectionsMenu from './ActiveSubSectionsMenu'
import ActiveTertiaryItemsMenu from './ActiveTertiaryItemsMenu'
import ArticleCard from '#src/components/ArticleCard'
import ArrowRight from '#src/components/Header/StandardHeader/icons/arrow-right.svg'
import CloseIcon from '#src/components/Header/StandardHeader/icons/mobile/close.svg'
import styles from './styles.css'

interface BrowseMenuProps {
  onClose: () => void
  isBrowseExpanded: boolean
}

const BrowseMenu: React.FC<BrowseMenuProps> = ({ isBrowseExpanded, onClose }) => {
  const { myself } = useContext(IdentityContext)
  const navContext = useNavigationContext()
  const analytics = useContext(AnalyticsContext)
  const history = useHistory()

  /**
   * We mount the mobile menu so that we can prefetch the navigation items for when the menu does
   * expand. Due to a bug with react-spring not garbage collecting correctly, we only want to
   * show the navigation lists (the animated portion) when the component itself is mounted.
   *
   * We should be able to safely use react-spring's latest version when this is merged:
   * https://github.com/react-spring/react-spring/pull/1054
   */
  const [isBrowseMenuMounted, setIsBrowseMenuMounted] = useState(false)
  useEffect(() => {
    setIsBrowseMenuMounted(true)
  }, [])

  const [isSearchActive, setIsSearchActive] = useState(false)
  const scrollableContainerRef = useRef<HTMLDivElement>(null)

  const { data: journalData } = useMobileJournalNavigationQuery()
  const { data: fullBrowseData } = useFullBrowseNavHierarchyQuery()

  useEffect(() => {
    if (isBrowseExpanded) {
      myself?.user.isAllowedToRefer &&
        analytics?.events.trackReferralPageEntryPointViewed({
          entry_point: 'explore-menu'
        })
    }

    navContext?.onToggleIsMobileAccountMenuVisible(false)
    navContext?.onSetActiveDepartmentSlug(null)
  }, [isBrowseExpanded])

  const resetScroll = () => {
    /**
     * If the first child of the ScrollLock has a ref attribute, it will be overriden by ScrollLock's manipulation
     * of it's direct child. If we add another div inbetween the ScrollLock and scrollable container, scrolling
     * doesn't work at all on iOS. With this solution, we find the scrollable container via a data attribute and a
     * ref from parent ABOVE the ScrollLock. This reaches in and prevents the ref collision.
     */
    scrollableContainerRef.current
      ?.querySelector('[data-name="scrollable-container"]')
      ?.scrollTo({ top: 0 })
  }

  /**
   * Without the query, we can't render anything, hide the entire menu
   */
  if (!fullBrowseData) {
    return null
  }

  const flattenedDepartments = constructFlatDepartments(fullBrowseData.departmentsForNavigation)
  const departmentDataList = flattenedDepartments.map(flatDepartment => {
    return {
      onClick: () => {
        navContext?.onSetActiveDepartmentSlug(flatDepartment.referenceSlug)
        resetScroll()
      },
      children: (
        <>
          <span className={styles.listItemLabel}>{flatDepartment.displayName}</span>
          <ArrowRight className={styles.listItemIcon} />
        </>
      )
    }
  })

  /**
   * Journal menu
   */
  const journalDataList: MobileNavigationItem[] = [
    {
      to: '/journal',
      onClick: () => {
        analytics?.events.trackArticleHubEntryPointClicked({
          entry_point: 'header'
        })
        onClose()
      },
      children: (
        <>
          <span className={styles.listItemLabel}>View all</span>
          <ArrowRight className={styles.listItemIcon} />
        </>
      )
    }
  ]

  if (journalData) {
    journalDataList.unshift(
      ...journalData.searchArticles.edges.map<MobileNavigationItem>(journalEntry => ({
        onClick: () =>
          analytics?.events.trackArticleEntryPointClicked({
            entry_point: 'header',
            article_id: journalEntry.node.articleId
          }),
        children: (
          <ArticleCard
            className={styles.listItemArticleCard}
            article={journalEntry.node}
            layout="horizontal"
            sizes="25vw"
            onClick={() => {
              onClose()
            }}
          />
        )
      }))
    )
  }
  /**
   * Account menu, don't show it if we're not logged in
   */
  const accountDataList = myself
    ? [
        {
          onClick: () => {
            navContext?.onToggleIsMobileAccountMenuVisible(true)
            resetScroll()
          },
          children: (
            <>
              <span className={styles.listItemLabel}>{myself?.user.firstName}</span>
              <ArrowRight className={styles.listItemIcon} />
            </>
          )
        },
        {
          to: '/curations',
          children: (
            <span className={styles.listItemLabel} data-testid="mobile-navigation-curations">
              Curations
            </span>
          ),
          onClick: () => onClose()
        },
        {
          to: '/support',
          children: <span className={styles.listItemLabel}>Customer Support</span>,
          onClick: () => onClose()
        }
      ]
    : []

  /**
   * About menu
   */
  const aboutDataList = [
    {
      to: '/about-us',
      children: <span className={styles.listItemLabel}>About us</span>,
      onClick: () => onClose()
    },
    {
      to: '/meet-the-experts',
      children: <span className={styles.listItemLabel}>Meet the experts</span>,
      onClick: () => onClose()
    },
    {
      to: '/support',
      children: <span className={styles.listItemLabel}>Help</span>,
      onClick: () => onClose()
    }
  ].concat(
    myself?.user.isAllowedToRefer
      ? [
          {
            to: generateReferAFriendUrl({ source: 'explore-menu' }),
            children: (
              <span className={styles.listItemLabel} data-testid="mobile-navigation-referrals">
                Get 10% off
              </span>
            ),
            onClick: () => onClose()
          }
        ]
      : []
  )

  const onNavItemSelected = (link: DepartmentNavigationLinkFragment) => {
    // Reset the menus and collapse whatever is open
    onClose()

    // Make sure to set search as 'inactive' so we don't leave the results open
    setIsSearchActive(false)

    switch (link.__typename) {
      case 'DepartmentNavigationCategoryLink':
      case 'DepartmentNavigationSearchLink':
      case 'DepartmentNavigationDealsLink':
        const queryParams = getQueryParamsFromDeptNavLink(link)
        const linkLocation =
          queryParams &&
          serializeSearchQuery({
            categorySlug: queryParams.categorySlug,
            selections: queryParams.facets ? keyBy(queryParams.facets, 'name') : undefined,
            filters: queryParams.filters,
            entryPoint: 'nav-browse'
          })

        if (linkLocation) {
          history.push(linkLocation)
        }
        break

      case 'DepartmentNavigationCreatePathLink':
        history.push(
          generateCreateUrl({
            maybePathSlug: link.pathSlug,
            sourceKey: 'header-mobile-navigation-link',
            pathIntent: 'register'
          })
        )
        break

      case 'DepartmentNavigationDepartmentLink':
        history.push(`/d/${link.department.slug}`)
        break
    }
  }

  const isTopLevelScrollLocked =
    navContext?.activeDepartmentSlug ||
    navContext?.activeCategory ||
    navContext?.activeSubSection ||
    navContext?.isMobileAccountMenuVisible

  if (departmentDataList) {
    return (
      // When ScrollLock is active, the ref from it's immediate child is overridden
      <div ref={scrollableContainerRef}>
        <ScrollLock isActive={isBrowseExpanded}>
          <div
            data-name="scrollable-container"
            className={classNames(styles.browse, {
              [styles.lockScroll]: isTopLevelScrollLocked
            })}
            style={{
              opacity: isBrowseExpanded ? 1 : 0,
              visibility: isBrowseExpanded ? 'visible' : 'hidden'
            }}
            data-testid="mobile-browse-menu"
          >
            <Grid>
              <GridItem col={2} className={styles.navigation}>
                <div
                  className={styles.closeIcon}
                  onClick={() => onClose()}
                  data-testid="mobile-browse-menu-close"
                >
                  <CloseIcon />
                </div>

                <div className={styles.headerGroup}>Browse</div>
              </GridItem>
              <GridItem col={2}>
                <Search
                  onClose={onClose}
                  flatDepartments={flattenedDepartments}
                  onSetIsSearchActive={(isActive: boolean) => setIsSearchActive(isActive)}
                />
              </GridItem>
              <GridItem col={2}>
                <div
                  className={classNames(styles.menus, {
                    [styles.hidden]: isSearchActive
                  })}
                >
                  {isBrowseMenuMounted && (
                    <>
                      <NavigationList
                        header="Outdoors"
                        navigationItems={departmentDataList}
                        testId="mobile-department-list"
                      />
                      <div className={styles.dynamicList}>
                        <NavigationList
                          header="Expert journal"
                          navigationItems={journalDataList}
                          testId="mobile-expert-journal"
                        />
                      </div>
                      <div className={styles.accountList}>
                        {myself && (
                          <NavigationList
                            header="Account"
                            navigationItems={accountDataList}
                            testId="mobile-account-menu"
                          />
                        )}
                      </div>
                      <div className={myself ? styles.dynamicList : styles.accountList}>
                        <NavigationList
                          header="About Curated"
                          navigationItems={aboutDataList}
                          testId="mobile-about-curated"
                        />
                      </div>
                    </>
                  )}

                  {myself && (
                    <AccountMenu
                      onNavItemSelected={() => {
                        onClose()
                        navContext?.onToggleIsMobileAccountMenuVisible(false)
                      }}
                    />
                  )}
                  <ActiveCategoriesMenu
                    flatDepartments={flattenedDepartments}
                    onNavItemSelected={onNavItemSelected}
                  />
                  <ActiveSubSectionsMenu onNavItemSelected={onNavItemSelected} />
                  <ActiveTertiaryItemsMenu onNavItemSelected={onNavItemSelected} />
                </div>
              </GridItem>
            </Grid>
          </div>
        </ScrollLock>
      </div>
    )
  }

  return null
}

export default BrowseMenu
